/**
 * External-panel provider registry, the single source of truth for every
 * third-party research panel we send/receive traffic to.
 *
 * One descriptor per panel captures everything the rest of the stack needs so
 * the knowledge stops being smeared across the apply page, the redirect
 * builder, the payout task, and get-details:
 *   - which inbound query param(s) carry the per-participant id, and where we
 *     store it on the invitation (typed column vs rawSearchParams key);
 *   - how the panel is told the outcome (participant redirect URL, and whether
 *     it distinguishes complete vs unqualify);
 *   - whether the panel pays the participant (Terac must not);
 *   - how the outcome is delivered (participant redirect vs a server-side API).
 *
 * Kept pure (no db/env/network) so it is trivially testable; callers pass in
 * the env-derived bits (UI sandbox, Respondent completion URL).
 *
 * Prolific is intentionally NOT here: it uses completion codes on the Study,
 * not a redirect/param, and is handled separately.
 */

// Import from the client-safe `/constants` subpath, NOT `@repo/db` (whose entry
// is `server-only`), because client components also consume this registry.
import type { RecruitmentChannel } from "@repo/db/constants";
import { EXTERNAL_PAYMENT_CHANNELS } from "@repo/db/constants";

import type { LinkRedirectConfig } from "./link-redirects";
import { interpolateRedirectTemplate } from "./link-redirects";

/** Stable identifier for a panel (used as the map key and in logs/metrics). */
export type ExternalPanelKey =
  | "MAVEN"
  | "USER_INTERVIEWS"
  | "RESPONDENT"
  | "NEWTON"
  | "CINT"
  | "NETQUEST"
  | "EMPORIA"
  | "PURE_PROFILE"
  | "RDSECURED";

/** Where the per-participant id lives on the StudyInvitation. */
export type ProviderIdStorage =
  | "mavenSubmissionId"
  | "uiParticipantId"
  | "respondentResponseId"
  | "rawSearchParams";

/**
 * How the panel learns the participant's outcome.
 *  - participant_redirect: the participant's browser is sent back to the panel
 *    (for most panels the success redirect IS what triggers their approval).
 *  - server_api: we call the panel's API on approve/reject (Respondent). Works
 *    for async/manual outcomes where the participant is no longer in-browser.
 */
export type OutcomeDelivery = "participant_redirect" | "server_api";

export interface RedirectContext {
  readonly isCompleted: boolean;
  /** Respondent stores its completion URL per project, not in the URL. */
  readonly respondentCompletionUrl?: string | null;
  /** User Interviews sandbox vs prod base URL. */
  readonly userInterviewsUseSandbox?: boolean;
}

export interface ExternalPanelDescriptor {
  readonly key: ExternalPanelKey;
  readonly label: string;
  /** RecruitmentChannel this panel maps to, when it has one. */
  readonly channel: RecruitmentChannel | null;
  /**
   * Inbound query params (in priority order) the panel appends at redirect
   * time carrying the per-participant id.
   */
  readonly inboundParams: readonly string[];
  readonly idStorage: ProviderIdStorage;
  /** Panel pays the participant → Terac must not issue a ledger payout. */
  readonly paidExternally: boolean;
  readonly outcomeDelivery: readonly OutcomeDelivery[];
  /**
   * True when the panel exposes distinct complete vs unqualify redirect URLs
   * (so it should be redirected on ANY terminal). False when it only has a
   * completion callback (redirect on success only; firing it on a screen-out
   * would falsely mark the participant complete).
   */
  readonly hasUnqualifyRedirect: boolean;
  /**
   * Build the redirect URL for this panel, or null when the id is absent or
   * the required context (e.g. Respondent completion URL) is missing.
   */
  readonly buildRedirectUrl: (
    id: string,
    ctx: RedirectContext,
  ) => string | null;
}

const PANELS: readonly ExternalPanelDescriptor[] = [
  {
    key: "MAVEN",
    label: "Maven",
    channel: "MAVEN",
    inboundParams: ["id"],
    idStorage: "mavenSubmissionId",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      `https://app.maven.co/panel-offer?status=${
        isCompleted ? "complete" : "unqualify"
      }&id=${id}`,
  },
  {
    key: "USER_INTERVIEWS",
    label: "User Interviews",
    channel: "USER_INTERVIEWS",
    inboundParams: ["iid", "ui_participant_id"],
    idStorage: "uiParticipantId",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: false,
    buildRedirectUrl: (id, { isCompleted, userInterviewsUseSandbox }) => {
      if (!isCompleted) {
        return null;
      }
      const base = userInterviewsUseSandbox
        ? "https://sandbox.userinterviews.com"
        : "https://www.userinterviews.com";
      return `${base}/project-task-events/custom_link_with_redirect/complete?iid=${id}`;
    },
  },
  {
    key: "RESPONDENT",
    label: "Respondent",
    channel: "RESPONDENT",
    inboundParams: ["respondent_screener_response_id", "screener_response_id"],
    idStorage: "respondentResponseId",
    paidExternally: true,
    // Respondent has approve/reject API endpoints (see notify-external-panel-
    // completion); the redirect is UX-only.
    outcomeDelivery: ["server_api", "participant_redirect"],
    hasUnqualifyRedirect: false,
    buildRedirectUrl: (_id, { isCompleted, respondentCompletionUrl }) =>
      isCompleted ? (respondentCompletionUrl ?? null) : null,
  },
  {
    key: "NEWTON",
    label: "Newton",
    channel: "NEWTON",
    inboundParams: ["seid", "SEID"],
    idStorage: "rawSearchParams",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      `https://surveys.expertresearchportal.com/surveys/${
        isCompleted ? "c" : "s"
      }/${id}`,
  },
  {
    key: "CINT",
    label: "Cint",
    channel: "CINT",
    inboundParams: ["REID", "CINT_REID"],
    idStorage: "rawSearchParams",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      isCompleted
        ? `https://notch.insights.supply/cb?token=2d81bd5c-28f1-48c0-b408-9dad85b83577&RID=${id}`
        : `https://samplicio.us/s/ClientCallBack.aspx?RIS=20&RID=${id}`,
  },
  {
    key: "NETQUEST",
    label: "Netquest",
    channel: "NETQUEST",
    inboundParams: ["ticket"],
    idStorage: "rawSearchParams",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      isCompleted
        ? `https://transit.nicequest.com/transit/participation?tp=co_0&c=ok&ticket=${id}`
        : `https://transit.nicequest.com/transit/participation?tp=fo_0&c=ok&ticket=${id}`,
  },
  {
    key: "EMPORIA",
    label: "Emporia",
    channel: "EMPORIA",
    inboundParams: ["emporiaId"],
    idStorage: "rawSearchParams",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      isCompleted
        ? `https://app.emporiaresearch.com/survey/qualified?uid=${id}`
        : `https://app.emporiaresearch.com/survey/qualityrejected?uid=${id}`,
  },
  {
    key: "PURE_PROFILE",
    label: "Pure Profile",
    channel: "PURE_PROFILE",
    inboundParams: ["ppid"],
    idStorage: "rawSearchParams",
    paidExternally: true,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      isCompleted
        ? `https://surveyengine.pureprofile.com/api/v1/universal/payment/completed?num=1&ppid=${id}`
        : `https://surveyengine.pureprofile.com/api/v1/universal/payment/disqualified?num=1&ppid=${id}`,
  },
  {
    // Research Defender has no RecruitmentChannel of its own and is identified purely
    // by the `rdud` param on any link. It's a fraud/verification router, NOT a
    // panel that pays the participant: `rdud` alone doesn't identify a paying
    // panel, so it must NOT skip Terac's payout (paidExternally: false). Only
    // the return redirect to rdsecured's callback applies. This matches the
    // pre-registry behavior where `rdud` was in the redirect builder but never
    // in the payout-skip / evidence key set.
    key: "RDSECURED",
    label: "Research Defender",
    channel: null,
    inboundParams: ["rdud"],
    idStorage: "rawSearchParams",
    paidExternally: false,
    outcomeDelivery: ["participant_redirect"],
    hasUnqualifyRedirect: true,
    buildRedirectUrl: (id, { isCompleted }) =>
      `https://www.rdsecured.com/return?inbound_code=${
        isCompleted ? "1000" : "2000"
      }&rdud=${id}`,
  },
] as const;

/** All descriptors keyed by ExternalPanelKey. */
export const EXTERNAL_PANELS: Readonly<
  Record<ExternalPanelKey, ExternalPanelDescriptor>
> = Object.fromEntries(PANELS.map((p) => [p.key, p])) as Record<
  ExternalPanelKey,
  ExternalPanelDescriptor
>;

/** All descriptors, in declaration order. */
export const EXTERNAL_PANEL_LIST = PANELS;

/** rawSearchParams keys that identify a raw-param-stored panel participant. */
// Raw-param keys that mark a submission as paid by an external panel (drives
// payout-skip + needsEvidence). Only panels that actually pay the participant
// (paidExternally) belong here. For example, Research Defender's `rdud` is a
// verification marker, not a payment, so it's excluded.
export const EXTERNAL_PANEL_RAW_PARAM_KEYS: readonly string[] = PANELS.filter(
  (p) => p.idStorage === "rawSearchParams" && p.paidExternally,
).flatMap((p) => p.inboundParams);

function getStringParam(
  searchParams: Record<string, string | string[] | undefined>,
  key: string,
): string | undefined {
  const value = searchParams[key];
  return Array.isArray(value) ? value[0] : value;
}

/** Read a panel's per-participant id from the request's search params. */
export function readPanelIdFromParams(
  panel: ExternalPanelDescriptor,
  searchParams: Record<string, string | string[] | undefined>,
): string | undefined {
  for (const param of panel.inboundParams) {
    const value = getStringParam(searchParams, param);
    if (value) {
      return value;
    }
  }
  return undefined;
}

/**
 * Resolve which panel a request came from by inspecting its search params.
 * Returns the first panel whose inbound param is present, or null. Used for
 * no-login detection and first-seen-source attribution.
 */
export function resolveExternalPanelByParams(
  searchParams: Record<string, string | string[] | undefined>,
): ExternalPanelDescriptor | null {
  for (const panel of PANELS) {
    if (readPanelIdFromParams(panel, searchParams)) {
      return panel;
    }
  }
  return null;
}

/** Resolve a panel by its RecruitmentChannel (null for raw-param-only panels). */
export function resolveExternalPanelByChannel(
  channel: string | null | undefined,
): ExternalPanelDescriptor | null {
  if (!channel) {
    return null;
  }
  return PANELS.find((p) => p.channel === channel) ?? null;
}

/**
 * Display names for channels that pay their own participants but have no PANELS
 * descriptor. A descriptor carries redirect and id-storage behavior, so a
 * channel we only ever need to NAME does not earn one: Prolific pays through its
 * own platform and Terac neither redirects to it nor reads an id back.
 *
 * Explicit rather than derived from the enum, so a channel added to
 * EXTERNAL_PAYMENT_CHANNELS without a name here resolves to null and its callers
 * fall back to "we cannot name the payer", instead of showing a participant
 * "PURE_PROFILE".
 */
const CHANNEL_ONLY_PAYER_LABELS: Readonly<Record<string, string>> = {
  PROLIFIC: "Prolific",
};

/**
 * What to CALL the party that pays this channel's participants, or null when we
 * cannot name one.
 *
 * Only for a channel that is itself proof of external payment. Membership in
 * EXTERNAL_PAYMENT_CHANNELS is a fact about the link, not an inference from a
 * stray query param, so naming its panel is safe; `isExternalPanelPaid` returning
 * true off a raw marker is NOT, and resolves through
 * `resolveExternalPanelFromInvitation` instead. Cint and Netquest are absent from
 * the channel set by design and stay on that marker path.
 */
export function resolveChannelPayerLabel(
  channel: string | null | undefined,
): string | null {
  if (!isExternalPaymentChannel(channel)) {
    return null;
  }
  const panel = resolveExternalPanelByChannel(channel);
  return panel?.label ?? CHANNEL_ONLY_PAYER_LABELS[channel as string] ?? null;
}

/**
 * The invitation's stored provider markers, matching the typed columns +
 * rawSearchParams bag the redirect/payout logic reads.
 */
export interface ExternalPanelInvitation {
  readonly uiParticipantId?: string | null;
  readonly respondentResponseId?: string | null;
  readonly mavenSubmissionId?: string | null;
  readonly metadata?: {
    readonly rawSearchParams?: Record<string, string>;
  } | null;
}

/** Read a panel's stored id off an invitation. */
export function readPanelIdFromInvitation(
  panel: ExternalPanelDescriptor,
  invitation: ExternalPanelInvitation,
): string | undefined {
  switch (panel.idStorage) {
    case "mavenSubmissionId":
      return invitation.mavenSubmissionId ?? undefined;
    case "uiParticipantId":
      return invitation.uiParticipantId ?? undefined;
    case "respondentResponseId":
      return invitation.respondentResponseId ?? undefined;
    case "rawSearchParams": {
      const raw = invitation.metadata?.rawSearchParams ?? {};
      for (const param of panel.inboundParams) {
        if (raw[param]) {
          return raw[param];
        }
      }
      return undefined;
    }
  }
}

/**
 * Resolve the panel an invitation belongs to (only one id is ever present).
 *
 * `recruitmentChannel` (the invitation's link channel) scopes raw-param panels
 * so a stray marker can't be honored on an unrelated link: rawSearchParams
 * stores the FULL, unscoped click params (for audit), so a `?seid=`/`?REID=`
 * left over on a non-Newton/non-Cint link would otherwise resolve to that panel
 * and wrongly trigger its redirect / payout skip. When the channel is provided,
 * a channel-backed raw-param panel only matches if the link IS that channel.
 * Typed-column panels (Maven/UI/Respondent) are already channel-scoped at write
 * time; channel-less panels (Research Defender / `rdud`) can't be gated and stay
 * param-only. Omitting the channel falls back to param-only matching.
 */
export function resolveExternalPanelFromInvitation(
  invitation: ExternalPanelInvitation,
  opts?: { recruitmentChannel?: string | null },
): ExternalPanelDescriptor | null {
  const channel = opts?.recruitmentChannel ?? null;
  for (const panel of PANELS) {
    if (!readPanelIdFromInvitation(panel, invitation)) {
      continue;
    }
    if (
      panel.idStorage === "rawSearchParams" &&
      panel.channel != null &&
      channel != null &&
      panel.channel !== channel
    ) {
      continue;
    }
    return panel;
  }
  return null;
}

export interface LinkConfiguredRedirectInput {
  /** The link's own redirect templates, when an operator configured any. */
  readonly linkRedirectConfig?: LinkRedirectConfig | null;
  /** Whether the participant completed successfully (vs terminated). */
  readonly isCompleted: boolean;
  /**
   * The participant's inbound click params, which the templates draw their
   * placeholder values from.
   */
  readonly interpolationParams?: Readonly<
    Record<string, string | undefined>
  > | null;
  readonly submissionId?: string | null;
  readonly studyId?: string | null;
}

/**
 * The URL a link's own configuration gives for this outcome, or null when the
 * link has no template for it or a placeholder can't be resolved.
 *
 * Deliberately independent of the panel registry: a link on a brand-new panel,
 * or on no known panel at all, resolves here, which is what lets an operator
 * onboard a panel without a deploy. `quotaFull` is not consulted yet; see
 * LINK_REDIRECT_OUTCOMES.
 */
export function resolveLinkConfiguredRedirectUrl(
  input: LinkConfiguredRedirectInput,
): string | null {
  const template = input.isCompleted
    ? input.linkRedirectConfig?.complete
    : input.linkRedirectConfig?.screenOut;
  if (!template) {
    return null;
  }
  return interpolateRedirectTemplate({
    template,
    params: input.interpolationParams,
    builtins: { submissionId: input.submissionId, studyId: input.studyId },
  });
}

export interface ExternalPanelRedirectInput extends Omit<
  LinkConfiguredRedirectInput,
  "isCompleted"
> {
  readonly invitation: ExternalPanelInvitation;
  /** Whether the participant completed successfully (vs terminated). */
  readonly isCompleted: boolean;
  /**
   * The invitation's link channel. Scopes raw-param panels so a stray marker on
   * an unrelated link doesn't trigger a redirect (see
   * resolveExternalPanelFromInvitation).
   */
  readonly recruitmentChannel?: string | null;
  readonly respondentCompletionUrl?: string | null;
  readonly userInterviewsUseSandbox?: boolean;
}

/**
 * The single redirect URL that applies to an invitation (only one panel id is
 * ever present). Panels with distinct complete/unqualify URLs redirect on any
 * terminal; callback-only panels (UI, Respondent) redirect only on success.
 * Returns null for non-panel invitations.
 *
 * The link's own configuration wins over the registry, and is checked before the
 * panel lookup so a link with no registry panel still redirects.
 */
export function resolveExternalPanelRedirectUrl(
  input: ExternalPanelRedirectInput,
): string | null {
  const configured = resolveLinkConfiguredRedirectUrl({
    linkRedirectConfig: input.linkRedirectConfig,
    isCompleted: input.isCompleted,
    interpolationParams:
      input.interpolationParams ??
      input.invitation.metadata?.rawSearchParams ??
      null,
    submissionId: input.submissionId,
    studyId: input.studyId,
  });
  if (configured) {
    return configured;
  }

  const panel = resolveExternalPanelFromInvitation(input.invitation, {
    recruitmentChannel: input.recruitmentChannel,
  });
  if (!panel) {
    return null;
  }
  if (!panel.hasUnqualifyRedirect && !input.isCompleted) {
    return null;
  }
  const id = readPanelIdFromInvitation(panel, input.invitation);
  if (!id) {
    return null;
  }
  return panel.buildRedirectUrl(id, {
    isCompleted: input.isCompleted,
    respondentCompletionUrl: input.respondentCompletionUrl,
    userInterviewsUseSandbox: input.userInterviewsUseSandbox,
  });
}

/**
 * Whether a submission is paid by an external panel (so Terac must NOT issue a
 * ledger payout). An operator's statement on the link wins outright: `true` is
 * the only signal available for a panel with no registry entry, which matches
 * neither rule below and would otherwise be paid by the panel AND by Terac.
 * `null` means nothing was stated, so the registry decides.
 * A channel in the external-payment set is always paid
 * externally (covers Prolific completion-code flows with no per-submission
 * marker). Otherwise a paying panel counts if EITHER its typed column is set OR
 * any of its inbound params appear in the full rawSearchParams bag. Read this
 * channel-INDEPENDENTLY on purpose.
 *
 * Why channel-independent: the typed provider id (mavenSubmissionId, etc.) is
 * nulled at write time on a link whose channel doesn't match the panel, so a
 * real panel participant routed through a mislabeled link would otherwise lose
 * the marker and get paid by BOTH the panel and Terac. Because rawSearchParams
 * now stores the click verbatim, the original inbound param (`id`, `iid`,
 * `REID`, …) survives and we honor it here so payout is never double-issued.
 *
 * TRADE-OFF: Maven's inbound param is the generic `?id=`, so honoring it
 * channel-independently means a stray `?id=` on non-panel organic traffic is
 * treated as panel-paid and skips Terac's payout (an under-pay risk). This is a
 * deliberate call: prefer never double-paying (unrecoverable) over a rare
 * skipped payout (recoverable via support). This only affects payout;
 * redirect stays channel-scoped, so a stray `?id=` never redirects to a panel.
 * Non-paying panels (Research Defender / rdud, paidExternally:false) are
 * excluded.
 */
/**
 * Whether a channel skips Terac's payout for EVERY participant, with no
 * dependency on what they arrived with.
 *
 * Not the same as "is a registry panel". Cint and Netquest are registry panels
 * marked `paidExternally` but are absent from EXTERNAL_PAYMENT_CHANNELS, so
 * their skip hangs on an inbound `REID` / `ticket` being present; a participant
 * who loses it gets paid twice. Those links need the per-link flag as much as an
 * unlisted panel does, so callers offering that control must branch on this, not
 * on the registry.
 */
export function isExternalPaymentChannel(channel?: string | null): boolean {
  return (
    channel != null &&
    EXTERNAL_PAYMENT_CHANNELS.has(channel as RecruitmentChannel)
  );
}

/**
 * Whether the participant on a link is paid by someone other than Terac, from
 * what is knowable BEFORE a submission exists: the operator's statement on the
 * link and the channel itself.
 *
 * The entry-time half of `isExternalPanelPaid`, which also honors the
 * per-participant markers an invitation carries. Callers that have an
 * invitation should use that one; this is for the apply landing, where the
 * visitor has not applied yet and only the link is known. Both apply the same
 * precedence, so the two never disagree on a link they can both be asked about.
 *
 * Drives what the apply flow may promise: a participant the panel pays must not
 * be shown Terac's figure, because it is not the money they will receive.
 */
export function isLinkExternallyPaid(params: {
  /** `StudyRecruitmentLink.externallyPaid`. */
  externallyPaid?: boolean | null;
  /** `StudyRecruitmentLink.channel`. */
  channel?: string | null;
}): boolean {
  if (params.externallyPaid === true) {
    return true;
  }
  // An explicit "Terac pays" suppresses the channel rule, exactly as it does in
  // `isExternalPanelPaid`.
  if (params.externallyPaid === false) {
    return false;
  }
  return isExternalPaymentChannel(params.channel);
}

export function isExternalPanelPaid(params: {
  /**
   * `StudyRecruitmentLink.externallyPaid`: true = the panel pays, false = Terac
   * pays, null/undefined = nothing stated, fall through to the registry.
   */
  linkExternallyPaid?: boolean | null;
  recruitmentChannel?: string | null;
  uiParticipantId?: string | null;
  respondentResponseId?: string | null;
  mavenSubmissionId?: string | null;
  rawSearchParams?: Record<string, string> | null;
}): boolean {
  if (params.linkExternallyPaid === true) {
    return true;
  }
  // An explicit "Terac pays" suppresses the channel rule, but NOT the
  // per-participant markers below: a participant carrying a paying panel's own
  // id demonstrably came from that panel and has already been paid by it, and
  // no operator setting on the link should be able to pay them a second time.
  if (
    params.linkExternallyPaid !== false &&
    isExternalPaymentChannel(params.recruitmentChannel)
  ) {
    return true;
  }
  const raw = params.rawSearchParams ?? {};
  const typedMarkers: Record<string, string | null | undefined> = {
    mavenSubmissionId: params.mavenSubmissionId,
    uiParticipantId: params.uiParticipantId,
    respondentResponseId: params.respondentResponseId,
  };
  return PANELS.some((panel) => {
    if (!panel.paidExternally) {
      return false;
    }
    const typedPresent =
      panel.idStorage !== "rawSearchParams" &&
      Boolean(typedMarkers[panel.idStorage]);
    const rawParamPresent = panel.inboundParams.some((p) => Boolean(raw[p]));
    return typedPresent || rawParamPresent;
  });
}

/**
 * The channels an operator can create a provider link for, derived from the
 * registry rather than listed: a panel without a channel (raw-param-only, e.g.
 * Research Defender) cannot be one.
 *
 * Exported because `internalV2.links.createCustom` accepts only these, and a
 * caller that publishes the full `RecruitmentChannel` enum instead advertises
 * a dozen channels the procedure refuses.
 */
export const EXTERNAL_PANEL_CHANNELS = PANELS.map(
  (panel) => panel.channel,
).filter((channel): channel is NonNullable<typeof channel> => channel != null);
